import { Alert, Col, Container, Form, Row, Card, Button as BsButton, OverlayTrigger, Tooltip, Badge } from "react-bootstrap";
import { InfoCircleFill, LightningChargeFill } from "react-bootstrap-icons";
import { Input } from "./Controls/Input";
import { Select } from "./Controls/Select";
import { SpotSyncTable } from "./Controls/SpotSyncTable";
import { createLocalDate, createLocalTime, showBrowserDate, showBrowserTime, showDuration } from "./Data/Date";
import { cents, Money, showMoney, toCents } from "./Data/Money";
import { hours, mins } from "./Data/Duration";
import { useForm } from "react-hook-form";
import { useState, useEffect, useMemo } from "react";
import { useOptionalParamIds, useParamIds } from "./Data/Common";
import { Duration, LocalDate, LocalTime } from "@js-joda/core";
import { Button } from "./Controls/Button";
import { useLoaderData, useNavigate, useSearchParams } from "react-router-dom";
import { PageTitle } from "./Controls/PageTitle";
import { RateProgram, RateProgramData, RateType, ValidationType, RateEntryDiscountType } from "./Data/ApiTypes";
import { Api, identity } from "./Data/Api";
import { loadResult } from "./Data/Result";
import { MoneyInput } from "./Controls/MoneyInput";
import { toast } from "react-toastify";

interface DurationRateForm {
    name:      string;
    startDate: string;
    startTime: string;
    endDate:   string | null;
    endTime:   string | null;
    inAfter:   string | null;
    inBefore:  string | null;
    outAfter:  string | null;
    outBefore: string | null;
    upTo:      number;
    units:     "minutes" | "hours";
    price:     number;
    percentDiscount: number;
    isEvent:   boolean;
    isValidation: boolean;
    isSpecialRate: boolean;
    rateType: RateType;
    canBeValidated: boolean;
    validationType: ValidationType;
    code: string;
    discountType: RateEntryDiscountType;
}

// Days of week enum matching C# backend
enum DaysOfWeek {
    Sunday    = 1 << 0,  // 1
    Monday    = 1 << 1,  // 2
    Tuesday   = 1 << 2,  // 4
    Wednesday = 1 << 3,  // 8
    Thursday  = 1 << 4,  // 16
    Friday    = 1 << 5,  // 32
    Saturday  = 1 << 6,  // 64
}

const ALL_DAYS = [
    DaysOfWeek.Sunday,
    DaysOfWeek.Monday,
    DaysOfWeek.Tuesday,
    DaysOfWeek.Wednesday,
    DaysOfWeek.Thursday,
    DaysOfWeek.Friday,
    DaysOfWeek.Saturday,
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Convert bit value to array of selected days
function decodeDays(bitValue: number | null): DaysOfWeek[] {
    if (bitValue === null) return [...ALL_DAYS];
    return ALL_DAYS.filter(day => (bitValue & day) === day);
}

// Convert array of selected days to bit value (or null if all days)
function encodeDays(selectedDays: DaysOfWeek[]): number | null {
    if (selectedDays.length === ALL_DAYS.length) return null;
    return selectedDays.reduce((acc, day) => acc | day, 0);
}

type ProgramConfigFormSlice = Pick<
    DurationRateForm,
    | "name"
    | "startDate"
    | "startTime"
    | "endDate"
    | "endTime"
    | "inAfter"
    | "inBefore"
    | "outAfter"
    | "outBefore"
    | "isEvent"
    | "isValidation"
    | "isSpecialRate"
    | "canBeValidated"
    | "validationType"
    | "code"
>;

function serializeRateEntries(entries: RateEntryKeyless[]): string {
    return JSON.stringify(
        [...entries]
            .sort((a, b) => a.upTo.compareTo(b.upTo))
            .map((e) => ({
                upTo: e.upTo.toString(),
                rateCents: toCents(e.rate),
                discountType: e.discountType,
            })),
    );
}

function serializeProgramConfig(
    form: ProgramConfigFormSlice,
    selectedDays: DaysOfWeek[],
    anyTimeOfDay: boolean,
): string {
    return JSON.stringify({
        name: form.name.trim(),
        startDate: form.startDate,
        startTime: form.startTime,
        endDate: form.endDate,
        endTime: form.endTime,
        inAfter: anyTimeOfDay ? null : form.inAfter,
        inBefore: anyTimeOfDay ? null : form.inBefore,
        outAfter: anyTimeOfDay ? null : form.outAfter,
        outBefore: anyTimeOfDay ? null : form.outBefore,
        isEvent: form.isEvent,
        isValidation: form.isValidation,
        isSpecialRate: form.isSpecialRate,
        canBeValidated: form.canBeValidated,
        validationType: form.validationType,
        code: form.validationType === ValidationType.Code ? form.code.trim().toUpperCase() : null,
        applicableDays: encodeDays(selectedDays),
    });
}

interface RateEntryKeyless {
    upTo: Duration;
    rate: Money;
    discountType: RateEntryDiscountType;
}

function formatDiscountDisplay(entry: RateEntryKeyless): string {
    if (entry.discountType === RateEntryDiscountType.Percent) {
        const pct = toCents(entry.rate) / 100;
        return `${pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(2)}% off`;
    }
    if (entry.discountType === RateEntryDiscountType.SetPrice) {
        return `Set to ${showMoney(entry.rate)}`;
    }
    return showMoney(entry.rate);
}

function getRateEntryCardLabels(opts: {
    isCodeValidationType: boolean;
    isDiscountValidationType: boolean;
    isPercentDiscountMode: boolean;
    isSetPriceMode: boolean;
    isFlatRate: boolean;
    isOverride: boolean;
    isEvent: boolean;
}) {
    if (opts.isCodeValidationType) {
        if (opts.isPercentDiscountMode) {
            return { heading: "Discount Percent", button: "Save Discount" };
        }
        if (opts.isSetPriceMode) {
            return { heading: "Checkout Price", button: "Save Checkout Price" };
        }
        return { heading: "Discount Amount", button: "Save Discount" };
    }
    if (opts.isDiscountValidationType) {
        return { heading: "Discount Amount", button: "Save Discount" };
    }
    if (opts.isFlatRate) {
        if (opts.isEvent) {
            return { heading: "Event Rate", button: "Add Event Rate" };
        }
        return { heading: "Flat Rate", button: "Add Flat Rate" };
    }
    if (opts.isOverride) {
        return { heading: "Override Rate", button: "Add Override Rate" };
    }
    return { heading: "Price Tier", button: "Add Tier" };
}

function canShowValidationDropdown(rateType: RateType) {
  return rateType === RateType.Hourly || rateType === RateType.Event;
}

function canShowValidationTypeDropdown(rateType: RateType) {
  return rateType === RateType.Validation;
}

function getBackendFlags(rateType: RateType) {
    return {
        isEvent: rateType === RateType.Event,
        isValidation: rateType === RateType.Validation,
        isSpecialRate: rateType === RateType.Override
    };
}

function getTabFromRateType(rateType: RateType): string {
    switch (rateType) {
        case RateType.Event: return 'event';
        case RateType.Validation: return 'validation';
        case RateType.Override: return 'hourly';
        default: return 'hourly';
    }
}

export function FacilityRateDurationPage() {
    const { facilityId } = useParamIds();
    const { rateId     } = useOptionalParamIds();
    const prg = useLoaderData() as RateProgram;
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Determine initial rate type based on tab parameter
    const tabParam = searchParams.get('tab');
    const getInitialRateType = () => {
        if (tabParam === 'event') return RateType.Event;
        if (tabParam === 'validation') return RateType.Validation;
        if (tabParam === 'override') return RateType.Override;
        return RateType.Hourly;
    };

    const initialRateType = getInitialRateType();
    const { isEvent, isValidation, isSpecialRate } = getBackendFlags(initialRateType);

    let initForm: DurationRateForm = {
        units:     "minutes",
        upTo:      0,
        price:     1,
        name:      "",
        startDate: showBrowserDate( LocalDate.now() ),
        startTime: showBrowserTime( LocalTime.now() ),
        endDate:   null,
        endTime:   null,
        inAfter:   null,
        inBefore:  null,
        outAfter:  null,
        outBefore: null,
        isEvent:   isEvent,
        isValidation: isValidation,
        isSpecialRate: isSpecialRate,
        rateType: initialRateType,
        canBeValidated: false,
        validationType: ValidationType.Fixed,
        code: "",
        discountType: RateEntryDiscountType.Flat,
        percentDiscount: 0,
    };
    let initRates: RateEntryKeyless[] = [];
    let verb = "Create";
    if( rateId !== undefined ) {
        verb      = "Edit";
        initRates = prg.entries.map(e => ({
            upTo: e.upTo,
            rate: e.rate,
            discountType: e.discountType ?? RateEntryDiscountType.Flat,
        }));
        const firstEntry = prg.entries[0];
        initForm = {
            units:     "hours",
            upTo:      0,
            price:     1,
            name:      prg.name,
            startDate: showBrowserDate( prg.start.toLocalDate() ),
            startTime: showBrowserTime( prg.start.toLocalTime() ),
            endDate:   prg.end ? showBrowserDate( prg.end.toLocalDate() ) : null,
            endTime:   prg.end ? showBrowserTime( prg.end.toLocalTime() ) : null,
            inAfter:   prg.inAfter   ? showBrowserTime( prg.inAfter   ) : null,
            inBefore:  prg.inBefore  ? showBrowserTime( prg.inBefore  ) : null,
            outAfter:  prg.outAfter  ? showBrowserTime( prg.outAfter  ) : null,
            outBefore: prg.outBefore ? showBrowserTime( prg.outBefore ) : null,
            isEvent:   prg.isEvent,
            isValidation: prg.isValidation,
            isSpecialRate: prg.isSpecialRate,
            rateType: prg.isSpecialRate ? RateType.Override : prg.isValidation ? RateType.Validation : prg.isEvent ? RateType.Event : RateType.Hourly,
            canBeValidated: prg.canBeValidated,
            validationType: prg.validationType,
            code: (prg.code ?? "").toUpperCase(),
            discountType: firstEntry?.discountType ?? RateEntryDiscountType.Flat,
            percentDiscount: firstEntry?.discountType === RateEntryDiscountType.Percent ? toCents(firstEntry.rate) / 100 : 0,
        };
    }

    const [ msg,   setMsg   ] = useState<string>();
    const [ rates, setRates ] = useState<RateEntryKeyless[]>( initRates );
    const [ inputKey, setInputKey ] = useState(0);
    const [ selectedDays, setSelectedDays ] = useState<DaysOfWeek[]>( 
        rateId !== undefined ? decodeDays(prg.applicableDays) : [...ALL_DAYS] 
    );

    // Any time of day = no in/out time restrictions
    const hasTimeRestrictions = rateId !== undefined && !!(initForm.inAfter || initForm.inBefore || initForm.outAfter || initForm.outBefore);
    const [ anyTimeOfDay, setAnyTimeOfDay ] = useState(!hasTimeRestrictions);

    const frm = useForm<DurationRateForm>( { defaultValues: initForm } );
    const { register, handleSubmit, watch, setValue, setError, formState: { errors }, clearErrors } = { ...frm };
    const vals = watch();

    const isDiscountValidationType = watch('validationType') == ValidationType.Discount;
    const isCodeValidationType = watch('validationType') == ValidationType.Code;
    const codeDiscountType = watch('discountType');
    const isPercentDiscountMode = isCodeValidationType && codeDiscountType === RateEntryDiscountType.Percent;
    const isSetPriceMode = isCodeValidationType && codeDiscountType === RateEntryDiscountType.SetPrice;

    const isFlatRate = vals.isEvent || vals.isValidation || vals.isSpecialRate;
    const isOverride = vals.rateType === RateType.Override;
    const isEditMode = rateId !== undefined;

    const initialRatesSnapshot = useMemo(
        () => serializeRateEntries(initRates),
        [rateId],
    );
    const initialConfigSnapshot = useMemo(
        () =>
            isEditMode
                ? serializeProgramConfig(initForm, decodeDays(prg.applicableDays), !hasTimeRestrictions)
                : "",
        [rateId],
    );
    const ratesSnapshot = useMemo(() => serializeRateEntries(rates), [rates]);
    const configSnapshot = useMemo(
        () => serializeProgramConfig(vals, selectedDays, anyTimeOfDay),
        [vals, selectedDays, anyTimeOfDay],
    );
    const hasUnsavedChanges =
        !isEditMode ||
        ratesSnapshot !== initialRatesSnapshot ||
        configSnapshot !== initialConfigSnapshot;

    const rateEntryCardLabels = useMemo(
        () => getRateEntryCardLabels({
            isCodeValidationType,
            isDiscountValidationType,
            isPercentDiscountMode,
            isSetPriceMode,
            isFlatRate,
            isOverride,
            isEvent: vals.isEvent,
        }),
        [isCodeValidationType, isDiscountValidationType, isPercentDiscountMode, isSetPriceMode, isFlatRate, isOverride, vals.isEvent],
    );

    const toggleDay = (day: DaysOfWeek) => {
        setSelectedDays(prev => {
            if (prev.includes(day)) {
                if (prev.length === 1) return prev;
                return prev.filter(d => d !== day);
            } else {
                return [...prev, day].sort((a, b) => ALL_DAYS.indexOf(a) - ALL_DAYS.indexOf(b));
            }
        });
    };

    const toggleAllDays = () => {
        if (selectedDays.length === ALL_DAYS.length) return;
        setSelectedDays([...ALL_DAYS]);
    };

    useEffect(() => {
        if (vals.name?.trim() && rates.length > 0) {
            setMsg(undefined);
        }
    }, [vals.name, rates.length]);

    function addRateEntry(): void {
        const toDur = vals.units === "hours" ? hours : mins;
        if( vals.upTo <= 0 && !isFlatRate) {
            setError( "upTo", { type: "min", message: "Up To must be greater than 0" } );
            return;
        }
        if (isFlatRate && rates.length > 0) {
            toast.error("This rate type can only have one rate entry");
            return;
        }
        if (vals.price <= 0 && !isPercentDiscountMode && !isSetPriceMode) {
            setError( "price", { type: "min", message: "Price must be greater than 0" } );
            return;
        }
        if (isSetPriceMode && vals.price < 0) {
            setError( "price", { type: "min", message: "Set price cannot be negative" } );
            return;
        }
        if (isPercentDiscountMode && (vals.percentDiscount <= 0 || vals.percentDiscount > 100)) {
            setError( "percentDiscount", { type: "min", message: "Percent must be between 0.01 and 100" } );
            return;
        }
        const findRes = rates.find( r => r.upTo.equals( toDur( vals.upTo ) ) )
        if( findRes ) {
            setError( "upTo", { type: "validate", message: "There is already an entry with that value" } );
            return;
        }
        const entryDiscountType = isCodeValidationType
            ? (isPercentDiscountMode ? RateEntryDiscountType.Percent : isSetPriceMode ? RateEntryDiscountType.SetPrice : RateEntryDiscountType.Flat)
            : RateEntryDiscountType.Flat;
        const entryRate = isPercentDiscountMode
            ? cents(Math.round(vals.percentDiscount * 100))
            : cents(vals.price);
        setRates( [...rates, { upTo: toDur( vals.upTo ), rate: entryRate, discountType: entryDiscountType }] );
        setValue( "upTo",  0 );
        setValue( "price", 0 );
        setValue( "percentDiscount", 0 );
        setInputKey(prev => prev + 1);
        clearErrors(["upTo", "units", "price", "percentDiscount"]);
    }

    function deleteRate( el: RateEntryKeyless ): void {
        setRates( rates.filter( r => r !== el ) );
        clearErrors(["upTo", "units", "price", "percentDiscount"]);
    }

    const showAddRateEntry = !(isFlatRate && rates.length >= 1);
    const codeField = register("code", {
        validate: (value) => {
            if (!isCodeValidationType) return true;
            const trimmed = (value ?? "").trim();
            if (!trimmed) return "Code is required";
            if (trimmed.length < 5) return "Code must be at least 5 characters";
            if (!/^[A-Z0-9]+$/.test(trimmed)) return "Code must contain only letters and numbers";
            return true;
        },
    });

    const onSubmit = async ( data: DurationRateForm ) => {
        if (!data.name.trim()) {
            setMsg("Rate name is required");
            return;
        }
        if (rates.length === 0) {
            setMsg("At least one rate entry is required");
            return;
        }
       
        setIsSubmitting(true);
        try {
            const req: RateProgramData = {
                facilityId: facilityId,
                name:       data.name,
                start:      createLocalDate( data.startDate ).atTime( createLocalTime( data.startTime ) ),
                end:        data.endDate && data.endTime ? createLocalDate( data.endDate ).atTime( createLocalTime( data.endTime ) ) : null,
                inAfter:    anyTimeOfDay ? null : (data.inAfter   ? createLocalTime( data.inAfter   ) : null),
                inBefore:   anyTimeOfDay ? null : (data.inBefore  ? createLocalTime( data.inBefore  ) : null),
                outAfter:   anyTimeOfDay ? null : (data.outAfter  ? createLocalTime( data.outAfter  ) : null),
                outBefore:  anyTimeOfDay ? null : (data.outBefore ? createLocalTime( data.outBefore ) : null),
                entries:    rates.map(r => ({
                    upTo: r.upTo,
                    rate: r.rate,
                    discountType: r.discountType,
                })),
                isEvent:    data.isEvent,
                isValidation: data.isValidation,
                isSpecialRate: data.isSpecialRate,
                applicableDays: encodeDays(selectedDays),
                canBeValidated: data.canBeValidated,
                validationType: data.validationType,
                code: isCodeValidationType ? data.code.trim() : null,
            }
            if( !rateId ) {
                loadResult( await Api.facilityRateCreate( facilityId!, req ), identity, identity );
            } else {
                loadResult( await Api.facilityRateUpdate( facilityId!, rateId!, req ), identity, identity );
            }
            const currentTab = getTabFromRateType(data.rateType);
            nav(`/facility/${facilityId}/rate?tab=${currentTab}`);
        } catch (err: any) {
            const message = typeof err === 'string' ? err : err?.message ?? 'Failed to save rate';
            toast.error(message);
        } finally {
            setIsSubmitting(false);
        }
    }

    const localDate = showBrowserDate( LocalDate.now() );

    return <Container fluid className="px-0 px-sm-3">
        <Form onSubmit={handleSubmit(onSubmit)}>
            <Row className="justify-content-start">
                <Col lg="8" sm="12">
                    <div className="d-flex align-items-center gap-3 mb-2">
                        <BsButton variant="link" className="p-0" onClick={() => {
                            const currentTab = getTabFromRateType(vals.rateType);
                            nav(`/facility/${facilityId}/rate?tab=${currentTab}`);
                        }}>
                            ← Back
                        </BsButton>
                        <PageTitle className="mb-0">
                            {isOverride
                                ? <>{verb} Special Rate </>
                                : <>{verb} Rate</>}
                        </PageTitle>
                    </div>

                    {isOverride && <>
                        <div className="text-muted mb-2" style={{ fontSize: "0.95rem" }}>
                            A special rate applies a flat charge for a specific date and time window, <strong>overriding your standard hourly
                            rate entries</strong> during that period. Ideal for events, holidays, or peak demand days.
                        </div>
                        <Alert variant="danger" className="d-flex align-items-start gap-2 py-2 mb-3" style={{ fontSize: "0.9rem" }}>
                            <LightningChargeFill className="mt-1 flex-shrink-0" />
                            <span>When a parker arrives during the window below, this flat rate will apply &mdash; the standard duration-based rates will be ignored.</span>
                        </Alert>
                    </>}

                    {/* Card 1: Name / Rate Type / Validation */}
                    <Card className="mb-3">
                        <Card.Body>
                            <Row className="g-2">
                                <Col xs={12} sm={isOverride ? 12 : 6}>
                                    <Input
                                        type="text"
                                        label="Name"
                                        {...register("name")}
                                    />
                                </Col>

                                {!isOverride && (
                                    <Col xs={6} sm={3}>
                                        <Form.Group controlId="rateType">
                                            <Form.Label>Rate Type</Form.Label>
                                            <Form.Select
                                                disabled={rates.length > 0}
                                                title={rates.length > 0 ? "Remove all rate entries to change rate type" : ""}
                                                value={watch("rateType")}
                                                onChange={(e) => {
                                                    const selected = Number(e.target.value) as RateType;
                                                    setValue("rateType", selected);
                                                    const { isEvent, isValidation, isSpecialRate } = getBackendFlags(selected);
                                                    setValue("isEvent", isEvent);
                                                    setValue("isValidation", isValidation);
                                                    setValue("isSpecialRate", isSpecialRate);
                                                }}
                                            >
                                                {Object.keys(RateType)
                                                    .filter((k) => isNaN(Number(k)) && k !== "Override")
                                                    .map((key) => (
                                                        <option key={key} value={RateType[key as keyof typeof RateType]}>
                                                            {key}
                                                        </option>
                                                    ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                )}

                                {canShowValidationDropdown(watch("rateType")) && (
                                    <Col xs={6} sm={3}>
                                        <Form.Group controlId="canBeValidated">
                                            <Form.Label>
                                                Can be validated?{' '}
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={<Tooltip id="can-be-validated-tooltip">When enabled, this rate can be discounted through validation</Tooltip>}
                                                >
                                                    <InfoCircleFill className="icon-shift-up" size={14} style={{ cursor: 'help', marginLeft: '4px' }} />
                                                </OverlayTrigger>
                                            </Form.Label>
                                            <Form.Select
                                                value={watch("canBeValidated") ? "true" : "false"}
                                                onChange={(e) => {
                                                    const val = e.target.value === "true";
                                                    setValue("canBeValidated", val, { shouldValidate: true, shouldDirty: true });
                                                }}
                                            >
                                                <option value="false">No</option>
                                                <option value="true">Yes</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                )}

                                {canShowValidationTypeDropdown(watch("rateType")) && (
                                    <Col xs={6} sm={3}>
                                        <Form.Group controlId="validationType">
                                            <Form.Label>
                                                Validation Type
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip id="validation-type-tooltip">
                                                            <strong>Fixed:</strong> Shareable link to a request form for approval.<br />
                                                            <strong>Discount:</strong> Shareable link or QR code that discounts the parking rate.<br />
                                                            <strong>Code:</strong> Unique code parkers enter at hourly checkout (flat, percent, or set price).
                                                        </Tooltip>
                                                    }
                                                >
                                                    <InfoCircleFill className="icon-shift-up" size={14} style={{ cursor: 'help', marginLeft: '4px' }} />
                                                </OverlayTrigger>
                                            </Form.Label>
                                            <Form.Select
                                                {...register("validationType", { setValueAs: (v) => Number(v) })}
                                                defaultValue={0}
                                            >
                                                <option value={0}>Fixed</option>
                                                <option value={1}>Discount</option>
                                                <option value={2}>Code</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                )}

                                {isCodeValidationType && (
                                    <Col xs={12} sm={6}>
                                        <Input
                                            type="text"
                                            label="Validation Code"
                                            {...codeField}
                                            onChange={(e) => {
                                                e.target.value = e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
                                                codeField.onChange(e);
                                            }}
                                            error={errors.code}
                                        />
                                    </Col>
                                )}

                                {isCodeValidationType && (
                                    <Col xs={6} sm={3}>
                                        <Form.Group controlId="discountType">
                                            <Form.Label>Discount Type</Form.Label>
                                            <Form.Select
                                                value={watch("discountType")}
                                                disabled={rates.length > 0}
                                                onChange={(e) => {
                                                    setValue("discountType", Number(e.target.value) as RateEntryDiscountType, { shouldValidate: true, shouldDirty: true });
                                                }}
                                            >
                                                <option value={RateEntryDiscountType.Flat}>Flat ($)</option>
                                                <option value={RateEntryDiscountType.Percent}>Percent (%)</option>
                                                <option value={RateEntryDiscountType.SetPrice}>Set Price ($)</option>
                                            </Form.Select>
                                            {rates.length > 0 && (
                                                <Form.Text className="text-muted">
                                                    Remove the discount below to change type
                                                </Form.Text>
                                            )}
                                        </Form.Group>
                                    </Col>
                                )}
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Card 2: Date / Time range */}
                    <Card className="mb-3">
                        <Card.Body>
                            <Row className="g-2">
                                <Col xs={6} sm={3}>
                                    <Input
                                        type="date"
                                        label="Start Date"
                                        className="mb-0"
                                        min={rateId === undefined ? localDate : undefined}
                                        {...register("startDate", { required: "Start Date is required" })}
                                        error={errors.startDate}
                                    />
                                </Col>
                                <Col xs={6} sm={3}>
                                    <Input
                                        type="time"
                                        label="Start Time"
                                        className="mb-0"
                                        {...register("startTime", { required: "Start Time is required" })}
                                        error={errors.startTime}
                                    />
                                </Col>
                                <Col xs={6} sm={3}>
                                    <Input type="date" label="End Date" className="mb-0" {...register("endDate")} error={errors.endDate} />
                                </Col>
                                <Col xs={6} sm={3}>
                                    <Input type="time" label="End Time" className="mb-0" {...register("endTime")} error={errors.endTime} />
                                </Col>
                                <Col xs={12}>
                                    <Form.Text className="text-muted">The date range the rate program should be applied.</Form.Text>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Card 3: Time-of-day restrictions + days */}
                    {!isFlatRate && (
                        <Card className="mb-3">
                            <Card.Body>
                                <div className="mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="anyTimeOfDay"
                                        label="Rates should apply any time of day"
                                        checked={anyTimeOfDay}
                                        onChange={(e) => setAnyTimeOfDay(e.target.checked)}
                                    />
                                    <Form.Text className="text-muted">
                                        {anyTimeOfDay ? "Rate applies regardless of entry/exit time." : "Optionally restrict by in/out times below."}
                                    </Form.Text>
                                </div>
                                <Row className="g-2 mb-3">
                                    <Col xs={6} sm={3}>
                                        <Input type="time" label="In After"
                                            disabled={anyTimeOfDay}
                                            {...register("inAfter")} error={errors.inAfter} />
                                    </Col>
                                    <Col xs={6} sm={3}>
                                        <Input type="time" label="In Before"
                                            disabled={anyTimeOfDay}
                                            {...register("inBefore")} error={errors.inBefore} />
                                    </Col>
                                    <Col xs={6} sm={3}>
                                        <Input type="time" label="Out After"
                                            disabled={anyTimeOfDay}
                                            {...register("outAfter")} error={errors.outAfter} />
                                    </Col>
                                    <Col xs={6} sm={3}>
                                        <Input type="time" label="Out Before"
                                            disabled={anyTimeOfDay}
                                            {...register("outBefore")} error={errors.outBefore} />
                                    </Col>
                                </Row>
                                <div>
                                    <Form.Label>Applicable Days</Form.Label>
                                    <div className="d-flex gap-2 flex-wrap align-items-center">
                                        {ALL_DAYS.map((day, index) => (
                                            <Form.Check
                                                key={day}
                                                type="checkbox"
                                                id={`day-${day}`}
                                                label={DAY_NAMES[index]}
                                                checked={selectedDays.includes(day)}
                                                onChange={() => toggleDay(day)}
                                                style={{ minWidth: '52px' }}
                                            />
                                        ))}
                                        <BsButton
                                            variant="link"
                                            size="sm"
                                            onClick={toggleAllDays}
                                            disabled={selectedDays.length === ALL_DAYS.length}
                                        >
                                            Select All
                                        </BsButton>
                                    </div>
                                    {selectedDays.length === ALL_DAYS.length && (
                                        <Form.Text className="text-muted">All days selected (will be saved as "any day")</Form.Text>
                                    )}
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Card 4: rate / discount entry */}
                    {showAddRateEntry && <Card className="mt-3">
                        <Card.Body>
                            <div className="h5 mb-3">
                                {rateEntryCardLabels.heading}
                            </div>
                            <Row className="g-2">
                                {!isFlatRate && <>
                                    <Col xs={6}>
                                        <Input type="number"
                                            label="Stay Up To"
                                            explanation="Time limit"
                                            {...register( "upTo", { valueAsNumber: true, required: "Up To is required" } )}
                                            error={undefined} />
                                    </Col>
                                    <Col xs={6}>
                                        <Select label="Time Units"
                                            {...register( "units", { required: true } )}
                                            error={undefined}>
                                            <option value="minutes">Minutes</option>
                                            <option value="hours"  >Hours  </option>
                                        </Select>
                                    </Col>
                                </>}
                                {isPercentDiscountMode ? (
                                    <Col xs={12}>
                                        <Input
                                            type="number"
                                            step="0.1"
                                            min={0.01}
                                            max={100}
                                            label="Discount Percent"
                                            {...register("percentDiscount", { valueAsNumber: true })}
                                            error={errors.percentDiscount}
                                        />
                                    </Col>
                                ) : (
                                    <Col xs={12} sm={isFlatRate ? 12 : 6}>
                                        <MoneyInput
                                            key={inputKey}
                                            icon="$"
                                            label={isSetPriceMode ? "Set Price" : isDiscountValidationType || isCodeValidationType ? "Discount Amount" : "Price"}
                                            {...register( "price", { valueAsNumber: true, required: "Price is required" } )}
                                            error={undefined} />
                                    </Col>
                                )}
                            </Row>

                            {(errors.upTo || errors.units || errors.price) && (
                                <div className="text-danger mt-2 mb-1">
                                    {errors.upTo?.message || errors.units?.message || errors.price?.message}
                                </div>
                            )}

                            <div className="d-flex justify-content-end">
                                <Button type="button" className="mt-3" onClick={addRateEntry}>
                                    {rateEntryCardLabels.button}
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>}

                    <PageTitle className="mt-3">
                        {isCodeValidationType || isDiscountValidationType ? 'Discount' : 'Rate'} Preview
                    </PageTitle>
                    <div className="text-secondary">
                        {isCodeValidationType
                            ? 'Checkout code and discount amount parkers receive at hourly checkout'
                            : isDiscountValidationType
                            ? 'The discount granted for this validation'
                            : isOverride
                                ? 'The flat rate that will override standard hourly rates during the specified window'
                                : isFlatRate
                                    ? `The flat rate for the ${vals.isEvent ? 'event' : 'validation'}`
                                    : 'Add rate entries to fill the duration based rate table'}
                    </div>

                    {rates.length > 0 && <SpotSyncTable className="mt-3">
                        <thead>
                            <tr>
                                {!isFlatRate && <th className="w-50 text-center">Up To</th>}
                                <th className="w-50 text-center">{isDiscountValidationType || isCodeValidationType ? "Discount" : "Rate"}</th>
                                <th className="w-50 text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rates.sort( ( a, b ) => a.upTo.compareTo( b.upTo ) ).map( el => <tr key={el.upTo.toString()}>
                                {!isFlatRate && <td className="text-center align-middle">
                                    {showDuration( el.upTo )}
                                </td>}
                                <td className="text-center align-middle">
                                    {formatDiscountDisplay(el)}
                                </td>
                                <td className="text-center align-middle">
                                    <Button onClick={() => deleteRate( el )}>
                                        Delete
                                    </Button>
                                </td>
                            </tr> )}
                        </tbody>
                    </SpotSyncTable>}

                    {msg && <Alert variant="danger" className="mt-3">{msg}</Alert>}

                    <div className="d-flex gap-2 my-5">
                        <Button className="w-50" type="button" variant="outline-secondary" onClick={() => {
                            const currentTab = getTabFromRateType(vals.rateType);
                            nav(`/facility/${facilityId}/rate?tab=${currentTab}`);
                        }}>
                            Cancel
                        </Button>
                        <Button
                            className="w-50 btn-main"
                            type="submit"
                            loading={isSubmitting}
                            disabled={isEditMode && !hasUnsavedChanges}
                        >
                            Submit
                        </Button>
                    </div>
                </Col>
            </Row>
        </Form>
    </Container>;
}
